import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_MAX_CHARS = 21;

type Phase = 'pending' | 'ready' | 'running' | 'settled';

export interface MyPlayMarqueeTextProps {
  text: string;
  maxChars?: number;
  className?: string;
}

/**
 * Display-only title/theme: ≤maxChars static; longer strings marquee the full
 * text once when the card becomes visible, then settle on the first maxChars
 * inside a clipped title column. Re-runs only after the element has left the
 * viewport (left-viewport gate — ignores IntersectionObserver flicker).
 */
const MyPlayMarqueeText: React.FC<MyPlayMarqueeTextProps> = ({
  text,
  maxChars = DEFAULT_MAX_CHARS,
  className,
}) => {
  const safeText = text ?? '';
  const needsMarquee = safeText.length > maxChars;
  const settledText = safeText.slice(0, maxChars);

  const wrapRef = useRef<HTMLSpanElement>(null);
  const trackRef = useRef<HTMLSpanElement>(null);
  const [phase, setPhase] = useState<Phase>('pending');
  const [distance, setDistance] = useState(0);
  const [durationSec, setDurationSec] = useState(5);
  // Gate: only start after a prior non-intersecting period (true on mount).
  const leftViewportRef = useRef(true);
  const phaseRef = useRef<Phase>('pending');

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    setPhase('pending');
    setDistance(0);
    leftViewportRef.current = true;
  }, [safeText, maxChars]);

  useLayoutEffect(() => {
    if (!needsMarquee || phase !== 'ready') return;
    const wrap = wrapRef.current;
    const track = trackRef.current;
    if (!wrap || !track) return;
    const overflow = Math.max(0, track.scrollWidth - wrap.clientWidth);
    setDistance(overflow);
    const sec = overflow <= 0 ? 0.01 : Math.min(8, Math.max(4, overflow / 45));
    setDurationSec(sec);
    requestAnimationFrame(() => setPhase('running'));
  }, [needsMarquee, phase, safeText]);

  useEffect(() => {
    if (!needsMarquee) return;
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setPhase('settled');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        // Fully left viewport → arm gate for next appearance (ignore flicker
        // while still intersecting / partially on screen).
        if (!entry.isIntersecting) {
          leftViewportRef.current = true;
          if (phaseRef.current === 'settled' || phaseRef.current === 'running') {
            setPhase('pending');
          }
          return;
        }

        // Still on-screen but not enough visibility yet — do not re-arm.
        if (entry.intersectionRatio < 0.45) return;

        // On-screen after a prior leave (or first mount): run marquee once.
        if (!leftViewportRef.current) return;
        if (phaseRef.current === 'running' || phaseRef.current === 'ready') return;

        leftViewportRef.current = false;
        setPhase('ready');
      },
      { threshold: [0, 0.45, 0.6, 1], root: null }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [needsMarquee, safeText]);

  const onAnimationEnd = () => {
    setPhase('settled');
  };

  if (!needsMarquee) {
    return <span className={className}>{safeText}</span>;
  }

  const showSettled = phase === 'settled';
  const runClass = phase === 'running' ? ' myplay-marquee--run' : '';
  const style = {
    ['--myplay-marquee-distance' as string]: `${distance}px`,
    ['--myplay-marquee-duration' as string]: `${durationSec}s`,
  } as React.CSSProperties;

  return (
    <span ref={wrapRef} className={`myplay-marquee${runClass}`.trim()} style={style}>
      <span
        ref={trackRef}
        className={`myplay-marquee__track ${className ?? ''}`.trim()}
        onAnimationEnd={showSettled ? undefined : onAnimationEnd}
      >
        {showSettled ? settledText : safeText}
      </span>
    </span>
  );
};

export default MyPlayMarqueeText;
